import React, { ChangeEvent, JSX, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { OwFnInput } from "../OwFnInput/OwFnInput";
import OwButton from "../OwButton/OwButton";
import { v4 } from "uuid";
import styled from "styled-components";

type Props = {
    /**
     * The lookup function for using the value of the input to find the options
     * @param value
     */
    lookupFn: (value: string) => void;
    /**
     * The options that are available to the user to select from
     */
    availableOptions: any[];
    /**
     * The selected option
     */
    selectedOption?: any;
    /**
     * The selected option function in which the selected item is emitted to
     * @param value
     */
    selectedOptionFn: (value: any) => void;
    /**
     * Configure how the selection should be emitted to the selectedOptionFn
     */
    emitSelectionSetup?: {
        /**
         * Emits the entire item object (ignores the valueProp)
         */
        emitObject?: boolean;
        /**
         * Emits the items value, based on the valueProp
         */
        emitValue?: boolean;
    };
    /**
     * The title value for the item to show
     */
    titleProp?: string;
    /**
     * The sub title value for the item to show
     */
    subTitleProp?: string;
    /**
     * The value property for the item
     */
    valueProp?: string;
    /**
     * The render function for the item (if need custom setup)
     * @param item
     */
    renderFn?: (item: any) => JSX.Element;
    maxHeight?: number;
    isScrollable?: boolean;
    placeholder?: string;
    /**
     * A flag to signal resetting the typeahead. Must be used with the callback fn.
     */
    resetTypeahead?: boolean;
    resetTypeaheadCallbackFn?: () => void;
    clearSelectionFn?: () => void;
    wrapperClassName?: string;
    inputClassName?: string;
    /**
     * Display a '+' button that requires the user to confirm the selection before emitting.
     */
    confirmAdd?: boolean;

    disabled?: boolean;
    /**
     * Render the dropdown via a React Portal to escape overflow clipping contexts.
     * Use when the TypeaheadFn is inside a scrollable container (e.g. table-responsive).
     */
    usePortal?: boolean;
};

export const TypeaheadFn = (props: Props) => {
    const [showOptions, setShowOptions] = useState(false);
    const [inputVal, setInputVal] = useState("");
    const [selectedOption, setSelectedOption] = useState<any>(props.selectedOption);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const portalRef = useRef<HTMLDivElement>(null);
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

    const updateDropdownPosition = useCallback(() => {
        if (wrapperRef.current && props.usePortal) {
            const rect = wrapperRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom,
                left: rect.left,
                width: rect.width,
            });
        }
    }, [props.usePortal]);

    // Recalculate position on scroll/resize when portal dropdown is open
    useEffect(() => {
        if (showOptions && props.usePortal) {
            updateDropdownPosition();
            const handleScrollOrResize = () => updateDropdownPosition();
            window.addEventListener("scroll", handleScrollOrResize, { capture: true, passive: true });
            window.addEventListener("resize", handleScrollOrResize, { passive: true });
            return () => {
                window.removeEventListener("scroll", handleScrollOrResize, { capture: true } as EventListenerOptions);
                window.removeEventListener("resize", handleScrollOrResize);
            };
        }
    }, [showOptions, props.usePortal, updateDropdownPosition]);

    // Click-outside dismissal for portal mode
    useEffect(() => {
        if (showOptions && props.usePortal) {
            const handleClickOutside = (e: MouseEvent) => {
                const target = e.target as Node;
                if (!wrapperRef.current?.contains(target) && !portalRef.current?.contains(target)) {
                    setShowOptions(false);
                }
            };
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [showOptions, props.usePortal]);

    useEffect(() => {
        if (props.availableOptions.length) {
            setShowOptions(true);
        }
    }, [props.availableOptions]);

    useEffect(() => {
        if (props.resetTypeahead && props.resetTypeaheadCallbackFn) {
            setShowOptions(false);
            setInputVal("");
            setSelectedOption(undefined);
            // props.resetTypeaheadCallbackFn();
        }
    }, [props.resetTypeahead]);

    useEffect(() => {
        if (props.selectedOption) {
            if (props.titleProp) {
                setInputVal(props.selectedOption[props.titleProp]);
            }
        } else {
            setInputVal("");
        }
    }, [props.selectedOption]);

    const handleInput = (e: ChangeEvent<any>) => {
        // Clear reset flag first if active
        if (props.resetTypeahead && props.resetTypeaheadCallbackFn) {
            props.resetTypeaheadCallbackFn();
        }

        // Always process input normally
        const value = e.target.value;
        if (value !== inputVal) {
            props.lookupFn(value);
            setInputVal(value);
        }
    };

    const emitToParent = (option: any) => {
        if (props.emitSelectionSetup) {
            if (props.emitSelectionSetup.emitObject) {
                props.selectedOptionFn(option);
            } else if (props.emitSelectionSetup.emitValue) {
                // @ts-expect-error
                props.selectedOptionFn(option[props.valueProp]);
            }
        } else {
            props.selectedOptionFn(option);
        }
    };

    const emitSelectedOption = (option: any) => {
        if (!props.confirmAdd) {
            emitToParent(option);
        }

        setShowOptions(false);
        setSelectedOption(option);

        if (props.titleProp) {
            setInputVal(option[props.titleProp]);
        }
    };

    const addConfirmedOption = () => {
        if (selectedOption) {
            emitToParent(selectedOption);
            setSelectedOption(undefined);
            setInputVal("");
        }
    };

    const clearSelection = () => {
        if (props.clearSelectionFn) {
            props.clearSelectionFn();
        }
    };

    const optionItems = props.availableOptions.map((option) => {
        return (
            <div key={v4()}>
                {props.renderFn ? (
                    <Option
                        onClick={() => {
                            emitSelectedOption(option);
                        }}>
                        {props.renderFn(option)}
                    </Option>
                ) : (
                    <Option
                        onClick={() => {
                            emitSelectedOption(option);
                        }}>
                        <div>{props.titleProp ? option[props.titleProp] : option}</div>
                        {props.subTitleProp ? <div>{option[props.subTitleProp]}</div> : null}
                    </Option>
                )}
            </div>
        );
    });

    const renderDropdown = () => {
        if (!(props.availableOptions.length || []) || !showOptions) return null;

        if (props.usePortal && dropdownPosition) {
            return createPortal(
                <div ref={portalRef}>
                    <OptionsWrapper
                        style={{
                            position: "fixed",
                            top: dropdownPosition.top,
                            left: dropdownPosition.left,
                            width: dropdownPosition.width,
                            zIndex: 9999,
                        }}>
                        {optionItems}
                    </OptionsWrapper>
                </div>,
                document.body
            );
        }

        return <OptionsWrapper>{optionItems}</OptionsWrapper>;
    };

    return (
        <div ref={wrapperRef} className={"TypeaheadFn position-relative " + (props.wrapperClassName || "")}>
            <div className={props.confirmAdd ? "input-group" : ""}>
                <OwFnInput
                    fn={handleInput}
                    name={"typeahead"}
                    enableDebounce={true}
                    value={inputVal}
                    noOuterDiv={true}
                    placeholder={props.placeholder}
                    className={props.inputClassName}
                    disabled={props.disabled}
                />
                {props.confirmAdd ? (
                    <OwButton leftIconClass={"fas fa-plus"} title={"add"} fn={addConfirmedOption} disabled={!selectedOption} />
                ) : null}
            </div>
            {props.selectedOption && props.clearSelectionFn && !props.disabled ? (
                <ClearDiv>
                    <i className={"fas fa-times pointer"} onClick={clearSelection} />
                </ClearDiv>
            ) : null}
            {renderDropdown()}
        </div>
    );
};

const OptionsWrapper = styled.div`
    background-color: var(--main-bg-color);
    position: absolute;
    width: 100%;
    display: flex;
    flex-direction: column;
    font-size: 0.875rem;
    box-shadow: 0px 3px 3px 0px rgba(0, 0, 0, 0.34);
    //border: var(--bs-border-width) solid var(--bs-border-color);
    border-radius: var(--bs-border-radius-sm);
    z-index: 2;
`;

const Option = styled.div`
    width: 100%;
    padding: 0.25rem 0.5rem;
    line-height: 2.75rem;
    font-size: 1rem;

    &:hover {
        cursor: pointer;
        background-color: var(--table-hover-row);
    }
`;

const ClearDiv = styled.div`
    position: absolute;
    right: 0;
    top: 0;
    margin-right: 0.5rem;
    height: 31px;
    line-height: 31px;
`;
